<template>
  <div class="content-dashboard">
    <h3>Mes commandes</h3>

    <div class="row mb-5 mt-4">
      <div class="col-lg-12">
        <div class="d-flex gap-3 mb-3">
          <!-- Date After Filter -->
          <div class="date-picker-wrapper" @click.prevent="openDatePicker('after')">
            <a href="#" class="btn btn-outline-orange medium-btn">
              <i class="ti ti-calendar orange-color pe-2"></i>
              {{ filters.createdAtAfter ? formatDate({ date: filters.createdAtAfter }) : "Date de début" }}
            </a>
            <input
              type="date"
              v-model="filters.createdAtAfter"
              class="date-input"
              ref="dateAfterPicker"
              @change="fetchOrders"
            />
          </div>
          <!-- Date Before Filter -->
          <div class="date-picker-wrapper" @click.prevent="openDatePicker('before')">
            <a href="#" class="btn btn-outline-orange medium-btn">
              <i class="ti ti-calendar orange-color pe-2"></i>
              {{ filters.createdAtBefore ? formatDate({ date: filters.createdAtBefore }) : "Date de fin" }}
            </a>
            <input
              type="date"
              v-model="filters.createdAtBefore"
              class="date-input"
              ref="dateBeforePicker"
              @change="fetchOrders"
            />
          </div>
        </div>
        <template v-if="isLoadingOrders">
          <div class="loading">Chargement des commandes...</div>
        </template>
        <template v-else-if="orders.length > 0">
          <div class="gray-box d-block mb-3" v-for="order in orders" :key="order.tokenValue">
            <div class="accordion-block-box justify-content-between align-items-center d-flex gap-3">
              <div class="accordion-block-info">
                <div class="accordion-block-info-top d-flex align-items-center gap-3">
                  <p>
                    <i class="ti ti-calendar pe-2 blue-color"></i>
                    <small>{{ formatDate(order.checkout_completed_at) }}</small>
                  </p>
                   <span :class="['status-title', getShippingOrderStateClass(order.shipped)]">
                    {{ translateShippingOrderState(order.shipped) }}
                  </span>
                </div>

                <div class="accordion-block-info-bottom mt-3 mb-2">
                  <ul class="d-flex align-items-center gap-2">
                    <li>
                      <span class="font-bold font-16">{{ order.number }}</span>
                    </li>
                    <li>
                      <span class="font-bold font-16">
                        {{ order.nb_articles || 0 }} article{{ (order.nb_articles || 0) > 1 ? "s" : "" }}
                      </span>
                    </li>
                    <li>
                      <span class="font-bold font-16">{{ (order.total / 100).toFixed(2) }}€</span>
                    </li>
                  </ul>
                </div>

                <p class="font-15 position-relative ps-4 text-muted">
                  <i class="ti-location-pin blue-color pe-e font-20 left_3 position-absolute"></i>
                  {{ formatAddress(order.shippingAddress) }}
                </p>
              </div>

              <div class="actions-btns d-flex flex-column gap-2 w-25" >
                <a
                  href="#"
                  class="btn btn-orange"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#sidePanel"
                  @click.prevent="showOrderDetails(order.tokenValue)"
                >
                  Voir les détails
                </a>
                <a :href="order.urlSuivi" target="_blank" class="btn btn-outline-orange"  v-if="order.shipped">Suivre mon colis</a>
              </div>
            </div>
          </div>
          <!-- Pagination -->
          <div v-if="totalPages > 1" class="pagination mt-4 d-flex justify-content-center align-items-center">
            <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="pagination-btn prev">
              <i class="ti ti-angle-left"></i>
            </button>
            <div class="pagination-content">
              <div class="pagination-btns mt-4">
                <button
                  v-for="page in totalPages"
                  :key="page"
                  @click="changePage(page)"
                  :class="['pagination-btn-numbers', 'mx-1', { active: currentPage === page }]"
                >
                  {{ page }}
                </button>
              </div>
              <span class="pagination-info">Sur {{ totalPages }}</span>
            </div>
            <button
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="pagination-btn next"
            >
              <i class="ti ti-angle-right"></i>
            </button>
          </div>
        </template>
        <template v-else>
          <div class="alert alert-info">Aucune commande trouvée.</div>
        </template>
      </div>
    </div>
  </div>

  <!-- Offcanvas for order details -->
  <div class="offcanvas offcanvas-end w-35" tabindex="-1" id="sidePanel" aria-labelledby="sidePanelLabel">
    <div class="offcanvas-header justify-content-end">
      <button type="button" class="close-link" data-bs-dismiss="offcanvas" aria-label="Fermer">
        <i class="ti ti-close"></i>
      </button>
    </div>
    <template v-if="loadingOrderDetails">
        <div class="loading">Chargement des détails de la commande...</div>
    </template>
    <div class="offcanvas-body" v-else-if="selectedOrder">
      <div class="commandes-panel">
        <h3 class="mb-5 mx-4">Mes commandes</h3>

        <Carousel
          v-if="getOrderImages.length > 0"
          :items-to-show="1"
          :wrap-around="true"
          :autoplay="false"
          :pause-autoplay-on-hover="true"
          :pagination-enabled="true"
          :navigation-enabled="false"
        >
          <Slide v-for="(img, index) in getOrderImages" :key="index">
            <img :src="img" class="w-60" alt="Product image" />
          </Slide>
        </Carousel>
        <div v-else class="alert alert-info">Aucune image disponible.</div>

        <div class="commande-inner m-4">
          <div class="d-flex gap-3 align-items-center my-4">
            <p class="dynamic-margin">
              <i class="ti ti-calendar pe-2 blue-color"></i>
              <small>{{ formatDate(selectedOrder.checkout_completed_at) }}</small>
            </p>
            <span :class="['status-title', getShippingOrderStateClass(selectedOrder.shipped)]">
                    {{ translateShippingOrderState(selectedOrder.shipped) }}
                  </span>

          </div>

          <div class="price-commande">
            <table class="table">
              <thead>
                <th>{{ selectedOrder.number || "N/A" }}</th>
                <th></th>
                <th class="text-end">
                  {{ (selectedOrder.items || []).length }} article{{
                    (selectedOrder.items || []).length > 1 ? "s" : ""
                  }}
                </th>
              </thead>
              <tbody>
                <tr v-for="item in selectedOrder.items || []" :key="item.id">
                  <td class="px-0">
                    <p class="dynamic-margin">
                      {{ item.productName || "N/A" }} ( {{ item.quantity }} )
                    </p>
                    <!-- <span class="dark-color-1 text-uppercase font-15">
                      REF: {{ item.variantDetails?.ref || "N/A" }}
                    </span> -->
                  </td>
                  <td class="px-0 w-30">
                    <small class="poids">{{ item.variantDetails?.poidsNet || "N/A"}}</small>
                  </td>
                  <td class="px-0 text-end">
                    <p class="dynamic-margin" v-if="item.subtotal===0">Offert</p>
                    <p class="dynamic-margin" v-else>{{ (item.subtotal / 100).toFixed(2) }}€</p>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="d-flex justify-content-between align-items-center mt-2">
               <div class="final-price-note">
                  <strong>Total TVA inclus</strong>
                </div>
                  <div class="final-price">
                  <strong>{{ (selectedOrder.total / 100).toFixed(2) }}€</strong>
                </div>
              
            </div>

           

            <div class="adress-commande mt-2">
              <p class="dynamic-margin">
                <i class="ti ti-location-pin blue-color"></i>
                {{ formatAddress(selectedOrder.shippingAddress) }}
              </p>
            </div>

            <div class="mt-4 d-flex gap-3">
              <a href="#" class="btn btn-orange" @click.prevent="reorder">Recommander</a>
              <!-- Factures buttons -->
              <template v-if="selectedOrder.factures && selectedOrder.factures.length > 0">
                <a
                  v-for="facture in selectedOrder.factures"
                  :key="facture.facture_pino"
                  href="#"
                  class="btn btn-outline-orange"
                  :class="{ disabled: downloadingFactures[facture.facture_pino] }"
                  @click.prevent="downloadFacture(facture.facture_pino, facture.facture_prefpino)"
                >
                  <i
                    v-if="downloadingFactures[facture.facture_pino]"
                    class="ti ti-loader pe-2 spinning me-2"
                  ></i>
                  <i v-else class="ti ti-download pe-2"></i>
                  <span>Télécharger la facture</span>
                </a>
              </template>
              <a v-else href="#" class="btn btn-outline-orange disabled">
                <i class="ti ti-download pe-2"></i>
                <span>Télécharger la facture</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="offcanvas-body">
      <div class="loading">Chargement des détails de la commande...</div>
    </div>
  </div>
</template>

<script>
  import { getOrders, getOrderDetails } from "@/api/orders";
  import { useCartStore } from "@/stores/cartStore";
  import { useFactureStore } from "@/stores/factureStore";
  import { useToast } from "vue-toastification";
  import { Carousel, Slide } from "vue3-carousel";
  import "vue3-carousel/carousel.css";

  export default {
    name: "CommandesView",
    components: {
      Carousel,
      Slide,
    },
    data() {
      return {
        orders: [],
        selectedOrder: null,
        isLoadingOrders: false,
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        filters: {
          createdAtAfter: "",
          createdAtBefore: "",
        },
        loadingOrderDetails: false,
        reorderError: null,
        reorderSuccess: false,
        downloadingFactures: {}, // Track downloading state for each facture
      };
    },
    computed: {
      getOrderImages() {
        if (!this.selectedOrder?.items) return [];
        const images = this.selectedOrder.items.flatMap(
          item => item.variantDetails?.imagesDetails?.map(img => img.path) || []
        );
        return [...new Set(images)];
      },
    },
    setup() {
      const factureStore = useFactureStore();
      const toast = useToast();
      return { factureStore, toast };
    },
    methods: {
      async fetchOrders() {
        this.isLoadingOrders = true;
        try {
          const params = {
            page: this.currentPage,
            itemsPerPage: this.itemsPerPage,
          };
          if (this.filters.createdAtAfter) {
            params["createdAt[after]"] = this.filters.createdAtAfter;
          }
          if (this.filters.createdAtBefore) {
            params["createdAt[before]"] = this.filters.createdAtBefore;
          }
          const response = await getOrders(this.currentPage, this.itemsPerPage, params);
          this.orders = Array.isArray(response.data["hydra:member"]) ? response.data["hydra:member"] : [];
          this.totalItems = response.data["hydra:totalItems"] || 0;
          this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
        } catch (error) {
          console.error("Erreur lors de la récupération des commandes:", error);
          this.orders = [];
          this.totalItems = 0;
          this.totalPages = 1;
        } finally {
          this.isLoadingOrders = false;
        }
      },
      async showOrderDetails(tokenValue) {
        this.loadingOrderDetails = true;
        this.selectedOrder = null; 
        const getfactures =true;
        try {
          const response = await getOrderDetails(tokenValue,getfactures );
          this.selectedOrder = response.data || null;
        } catch (error) {
          console.error("Erreur lors du chargement des détails de la commande:", error);
          this.selectedOrder = null;
        } finally {
          this.loadingOrderDetails = false;
        }
      },
      async downloadFacture(pino, prefPino) {
        // Set downloading state for this specific facture
        this.downloadingFactures[pino] = true;
        
        try {
          await this.factureStore.downloadFacture(pino, prefPino);
        } catch (error) {
          console.error("Erreur lors du téléchargement de la facture:", error);
          this.toast.error("Erreur lors du téléchargement de la facture");
        } finally {
          // Reset downloading state
          this.downloadingFactures[pino] = false;
        }
      },
      async reorder() {
        if (!this.selectedOrder?.items) {
          this.reorderError = "Aucune information sur les articles de la commande.";
          this.reorderSuccess = false;
          return;
        }

        const cartStore = useCartStore();
        this.reorderError = null;
        this.reorderSuccess = false;

        try {
          for (const item of this.selectedOrder.items) {
            const variantCode = item.variantDetails?.ref || item.productName;
            const quantity = item.quantity || 1;

            if (variantCode) {
              await cartStore.addToCart(variantCode, quantity);
            } else {
              console.warn(`Article sans variantCode: ${item.productName || "N/A"}`);
            }
          }

          this.reorderSuccess = true;
          this.$router.push({ name: "my-cart" });
        } catch (error) {
          console.error("Erreur lors de la réorganisation de la commande:", error);
          this.reorderError = "Une erreur s'est produite lors de l'ajout des articles au panier.";
          this.reorderSuccess = false;
        }
      },
      changePage(page) {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
          this.currentPage = page;
          this.fetchOrders();
        }
      },
      openDatePicker(type) {
        if (type === "after") {
          this.$refs.dateAfterPicker.showPicker();
        } else if (type === "before") {
          this.$refs.dateBeforePicker.showPicker();
        }
      },
      formatDate(dateObj) {
        if (!dateObj || !dateObj.date) return "--";

        try {
          const date = new Date(dateObj.date);
          if (isNaN(date)) return "--";
          return date.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        } catch (error) {
          console.error("Erreur lors du formatage de la date:", error);
          return "--";
        }
      },
      formatAddress(address) {
        if (!address) return "Adresse non disponible";
        console.log(address, "address formatAddress");

        const { street, city, postcode, provinceName, countryCode ,fullName } = address;

        const parts = [fullName,street, city, postcode, provinceName, countryCode === "FR" ? "France" : countryCode].filter(
          part => part && part.trim() !== ""
        );

        return parts.length > 0 ? parts.join(", ") : "Adresse non disponible";
      },

       translateShippingState(state) {
        const stateMap = {
          ready: "en cours de préparation",
          cancelled: "Annulé",
          shipped: "Expédié",
        };
        return stateMap[state] || "N/A";
      },
      getShippingStateClass(state) {
        const classMap = {
          ready: "blue-state",
          cancelled: "orange-state",
          shipped: "green-state",
        };
        return classMap[state] || "bg-light text-dark";
      },

       translateShippingOrderState(state) {
        const stateMap = {
          false: "en cours de préparation",
          true: "Expédié",
        };
        return stateMap[state] || "N/A";
      },
      getShippingOrderStateClass(state) {
        const classMap = {
          false: "blue-state",
          true: "green-state",
        };
        return classMap[state] || "bg-light text-dark";
      },
    },
    watch: {
      "filters.createdAtAfter": function () {
        this.currentPage = 1;
        this.fetchOrders();
      },
      "filters.createdAtBefore": function () {
        this.currentPage = 1;
        this.fetchOrders();
      },
    },
    mounted() {
      this.fetchOrders();
    },
  };
</script>

<style scoped>
  .commandes-panel table thead {
    border-bottom: 1px solid #dee2e6;
  }
  .left_3 {
    left: -3px;
  }
  .date-picker-wrapper {
    position: relative;
  }
  .date-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  .spinning {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid white;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .dynamic-margin{
    margin-bottom: 0;
  }

  .ready-state{

  }
  /* Responsive styles for offcanvas */
  @media (max-width: 767px) {
    .offcanvas.offcanvas-end {
      width: 100% !important;
    }

    .offcanvas-body {
      padding: 1rem;
    }

    .commandes-panel h3 {
      font-size: 1.25rem;
      margin-bottom: 1rem !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .commandes-panel .commande-inner {
      margin: 1rem 0 !important;
    }

    .commandes-panel .carousel {
      margin-bottom: 1rem;
    }

    .commandes-panel .carousel img {
      width: 100% !important;
      max-width: 100%;
    }

    .commandes-panel .d-flex.gap-3 {
      gap: 0.75rem !important;
      flex-wrap: wrap;
    }

    .commandes-panel .status-title {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
    }

    .commandes-panel .price-commande table {
      font-size: 0.875rem;
    }

    .commandes-panel .price-commande table th,
    .commandes-panel .price-commande table td {
      padding: 0.5rem 0.25rem;
    }

    .dynamic-margin {
      margin-bottom: 1rem ;
    }


    .commandes-panel .price-commande .poids {
      font-size: 0.75rem;
    }

    .commandes-panel .final-price-note,
    .commandes-panel .final-price {
      font-size: 0.875rem;
    }

    .commandes-panel .adress-commande p {
      font-size: 0.875rem;
    }

    .commandes-panel .btn {
      font-size: 0.875rem;
      padding: 0.5rem 1rem;
      width: 100%;
    }

    .commandes-panel .mt-4.d-flex.gap-3 {
      flex-direction: column;
      gap: 0.5rem !important;
    }

    .offcanvas-header {
      padding: 1rem;
    }

    .close-link {
      font-size: 1.5rem;
    }
  }


</style>